// components/Toast/index.tsx
'use client'
import { createRoot } from 'react-dom/client'
import styles from '@/styles/global/toast.module.scss'

// ================== 类型定义 ==================
type ToastOptions = {
  message: string
  icon?: React.ReactNode
  duration?: number
}

type ToastAPI = {
  show: (message: string, duration?: number) => void
}

type ToastStyle = React.CSSProperties & {
  '--toast-duration'?: string
}

// ================== 组件实现 ==================
const Toast = ({ message, duration = 2000 }: ToastOptions) => (
  <div
    className={styles.toastContainer}
    role="alert"
    style={{ '--toast-duration': `${duration}ms` } as ToastStyle}
  >
    <span className={styles.message}>{message}</span>
  </div>
)

// ================== 核心逻辑 ==================
let toastRoot: ReturnType<typeof createRoot> | null = null
let container: HTMLDivElement | null = null

const createToast = (options: ToastOptions) => {
  // SSR 环境下不执行
  if (typeof window === 'undefined') return { clear: () => {} }

  // 清理旧实例
  if (container) {
    toastRoot?.unmount()
    container.remove()
  }

  // 创建新容器
  container = document.createElement('div')
  document.body.appendChild(container)
  toastRoot = createRoot(container)

  // 渲染组件
  toastRoot.render(<Toast {...options} />)

  // 自动清除
  setTimeout(() => clear(), options.duration ?? 2000)

  return { clear }

  function clear() {
    toastRoot?.unmount()
    container?.remove()
    toastRoot = null
    container = null
  }
}

// ================== 业务API ==================
export const toast: ToastAPI = {
  show(message, duration = 2000) {
    createToast({
      message,
      duration
    })
  }
}

// ================== 默认导出组件（可选） ==================
export default Toast
